import type { BibleBook } from "./types";

/** Isaiah to Malachi — seventeen books of prophecy, major and minor. */
export const PROPHETS: BibleBook[] = [
  {
    slug: "isaiah",
    name: "Isaiah",
    testament: "old",
    genre: "prophecy",
    order: 23,
    chapters: 66,
    author:
      "Isaiah of Jerusalem, with later material widely attributed to other hands",
    written: "Eighth century BC onward, reaching final form after the exile",
    audience: "Judah before the catastrophe, and exiles inside it",
    argument:
      "Sixty-six chapters that change addressee halfway through — warning before the exile, and then comfort spoken to people already in it.",
    summary: [
      "Isaiah is the longest and most quoted prophetic book, and it reads as two or three books joined. Chapters 1 to 39 address Jerusalem before the Babylonian conquest, in the eighth century, largely in warning. From chapter 40 the audience has changed: the exile has happened, and the tone turns to comfort.",
      "That shift is why most scholars see more than one author, and why the book's second half contains its most-loved passages — the comfort chapter, the eagles' wings, the servant songs that the New Testament reads as pointing to Jesus.",
      "It is also the Old Testament book quoted most often in the New. Whatever its composition, the early church read it as the interpretive key to what had happened to them.",
    ],
    startHere:
      "Chapter 40, which is where the second half begins and where almost everything people know from Isaiah is located.",
    themes: ["judgement", "comfort", "the servant", "new creation"],
    keyChapters: [
      {
        chapter: 6,
        why: "The call vision in the temple, and the coal taken from the altar.",
      },
      {
        chapter: 40,
        why: "Comfort my people — where the book turns from warning to consolation.",
      },
      {
        chapter: 53,
        why: "The fourth servant song, quoted more often in the New Testament than any other Old Testament passage.",
      },
    ],
    known: {
      text: "but those who wait for Yahweh will renew their strength. They will mount up with wings like eagles. They will run, and not be weary. They will walk, and not faint.",
      ref: "Isaiah 40:31",
    },
    misread:
      "That 40:31 promises energy. The three images descend — flying, running, walking — and the climax is walking without fainting, which is a promise about endurance rather than about power.",
    faq: [
      {
        q: "How many chapters are in Isaiah?",
        a: "Sixty-six. Chapters 1 to 39 address Judah before the exile; from 40 onward the audience is people already exiled, which is why the tone changes so sharply.",
      },
      {
        q: "Did one person write Isaiah?",
        a: "Traditionally yes. Most modern scholarship sees at least two hands and often three, based on the change of historical setting at chapter 40 and differences in language.",
      },
    ],
    passages: ["isaiah-41-10", "isaiah-40-31"],
    topics: ["bible-verses-about-strength"],
  },
  {
    slug: "jeremiah",
    name: "Jeremiah",
    testament: "old",
    genre: "prophecy",
    order: 24,
    chapters: 52,
    author: "Jeremiah, with his scribe Baruch",
    written: "Late seventh to early sixth century BC",
    audience: "A city that did not want to hear it",
    argument:
      "Forty years of being right and being ignored, from a man who says plainly that he did not want the job and could not put it down.",
    summary: [
      "Jeremiah prophesied through the last decades of Judah and into the fall of Jerusalem. His message was that the city would fall and that resistance was futile, which made him a traitor in the eyes of most of his contemporaries. He was beaten, imprisoned, thrown into a cistern and finally taken to Egypt against his will.",
      "The book preserves his complaints as well as his oracles, and they are unusually raw — he accuses God of deceiving him, curses the day he was born, and says that when he tries to stop speaking the message becomes a fire in his bones.",
      "It also contains the promise of a new covenant written on hearts rather than tablets, which the New Testament takes up directly, and the letter to the exiles that includes the most-quoted and most-misapplied verse in the book.",
    ],
    startHere:
      "Chapter 29, the letter to the exiles, which is both the source of the famous verse and the correction to how it is used.",
    themes: ["warning", "lament", "new covenant", "exile"],
    keyChapters: [
      {
        chapter: 20,
        why: "The passage where he accuses God of deceiving him and says the message is a fire in his bones.",
      },
      {
        chapter: 29,
        why: "The letter to the exiles, containing the verse everybody quotes and the seventy years that reframe it.",
      },
      {
        chapter: 31,
        why: "The new covenant written on hearts, which Hebrews quotes at length.",
      },
    ],
    known: {
      text: 'For I know the thoughts that I think toward you," says Yahweh, "thoughts of peace, and not of evil, to give you hope and a future.',
      ref: "Jeremiah 29:11",
    },
    misread:
      "That 29:11 is a personal promise about your plans. It is written to a nation deported to Babylon, in a letter telling them to build houses and settle down because the exile will last seventy years — most of its first readers died in it.",
    faq: [
      {
        q: "What is Jeremiah 29:11 really about?",
        a: "It is part of a letter to exiles in Babylon telling them to settle there for seventy years. The promise is to a nation about a return most of the original readers would not live to see.",
      },
      {
        q: "Why is Jeremiah called the weeping prophet?",
        a: "Because the book preserves his laments alongside his oracles — including passages where he curses the day he was born and accuses God of deceiving him.",
      },
    ],
    passages: ["jeremiah-29-11"],
  },
  {
    slug: "lamentations",
    name: "Lamentations",
    testament: "old",
    genre: "prophecy",
    order: 25,
    chapters: 5,
    author: "Traditionally Jeremiah; formally anonymous",
    written: "Shortly after 586 BC",
    audience: "Survivors of a siege",
    argument:
      "Five acrostic poems written in the ruins of Jerusalem, which contain the line about mercies new every morning and give it a context nobody expects.",
    summary: [
      "Lamentations is five poems mourning the destruction of Jerusalem. Four of them are acrostics, each stanza beginning with successive letters of the Hebrew alphabet — a formal constraint imposed on grief, which is itself part of the meaning.",
      "The contents are not decorous. It describes famine, siege, the deaths of children and the humiliation of a city, in language that does not look away.",
      "In the exact middle, at the centre of the central poem, sits the passage about steadfast love that never ceases and mercies new every morning. It is not a change of subject; it is a man in the wreckage finding one thing he can still say, and the book returns to lament immediately afterwards.",
    ],
    startHere:
      "Chapter 3, which contains both the deepest despair and the famous lines, and shows how close together they sit.",
    themes: ["grief", "siege", "acrostic", "mercy"],
    keyChapters: [
      {
        chapter: 1,
        why: "The city personified as a widow sitting alone, which sets the book's voice.",
      },
      {
        chapter: 3,
        why: "The centre, where the mercies new every morning sit between two stretches of despair.",
      },
      {
        chapter: 5,
        why: "The closing prayer, which ends without resolution — restore us, unless you have utterly rejected us.",
      },
    ],
    known: {
      text: "It is because of Yahweh's loving kindnesses that we are not consumed, because his mercies don't fail. They are new every morning. Great is your faithfulness.",
      ref: "Lamentations 3:22–23",
    },
    misread:
      "That the famous verse is a cheerful morning devotional. It is spoken by a man who has just described the siege of his city and returns to lament in the next paragraph, which is what gives it its weight.",
    faq: [
      {
        q: "Who wrote Lamentations?",
        a: "Tradition assigns it to Jeremiah; the book itself is anonymous. It was written soon after Jerusalem's destruction in 586 BC by someone who had evidently seen it.",
      },
      {
        q: "Why is Lamentations an acrostic?",
        a: "Four of its five poems run through the Hebrew alphabet. The formal constraint holds the grief in a shape, which is widely read as part of the point rather than decoration.",
      },
    ],
    topics: ["bible-verses-about-grief"],
  },
  {
    slug: "ezekiel",
    name: "Ezekiel",
    testament: "old",
    genre: "prophecy",
    order: 26,
    chapters: 48,
    author: "Ezekiel, a priest deported to Babylon",
    written: "Sixth century BC",
    audience: "Exiles who thought the temple made them safe",
    argument:
      "The strangest book in the Old Testament — wheels, dry bones, a prophet lying on his side for over a year — written by a priest with no temple.",
    summary: [
      "Ezekiel was deported to Babylon before Jerusalem fell and prophesied to fellow exiles. He was a priest denied the one thing a priest does, and the book is preoccupied with the presence of God leaving the temple and, eventually, returning.",
      "It is also extraordinarily strange. The opening vision of wheels within wheels has generated centuries of interpretation. The prophet is instructed to perform his message physically — lying on one side for 390 days, shaving his head with a sword, cooking over dung — and told not to mourn publicly when his wife dies.",
      "The valley of dry bones in chapter 37 is the image everybody knows, and the last nine chapters are a detailed architectural vision of a temple that was never built.",
    ],
    startHere:
      "Chapter 37, the dry bones, which is the most accessible chapter and the book's clearest statement of hope.",
    themes: ["glory departing", "sign-acts", "judgement", "restoration"],
    keyChapters: [
      {
        chapter: 1,
        why: "The opening vision of wheels and living creatures, which has generated centuries of interpretation.",
      },
      {
        chapter: 34,
        why: "The shepherds who fed themselves rather than the sheep, which John's gospel later answers.",
      },
      {
        chapter: 37,
        why: "The valley of dry bones, the image everybody knows and the book's clearest hope.",
      },
    ],
    known: {
      text: "I will also give you a new heart, and I will put a new spirit within you. I will take away the stony heart out of your flesh, and I will give you a heart of flesh.",
      ref: "Ezekiel 36:26",
    },
    misread:
      "That the strange behaviour is incidental colour. The sign-acts are the message delivered physically, to an audience that had stopped listening to words.",
    faq: [
      {
        q: "What are the dry bones in Ezekiel?",
        a: "A vision in chapter 37 of a valley of bones being reassembled and given breath — explained in the text as a picture of exiled Israel being brought back to life as a people.",
      },
      {
        q: "Why is Ezekiel so strange?",
        a: "Because much of his message is delivered as physical performance rather than speech, to exiles who had stopped listening. The visions that frame the book are apocalyptic in style, which is a genre with its own conventions.",
      },
    ],
  },
  {
    slug: "daniel",
    name: "Daniel",
    testament: "old",
    genre: "apocalyptic",
    order: 27,
    chapters: 12,
    author: "Anonymous; the visions are widely dated to the second century BC",
    written:
      "Court tales possibly earlier; final form likely second century BC",
    audience: "Jews under a regime demanding they give up their practice",
    argument:
      "Six chapters of court stories about staying faithful under a foreign king, and six chapters of visions that founded a whole genre.",
    summary: [
      "Daniel divides cleanly. Chapters 1 to 6 are narratives — the food test, the fiery furnace, the writing on the wall, the lions' den — set in the Babylonian and Persian courts and concerned with how you keep your practice when the state objects to it.",
      "Chapters 7 to 12 are apocalyptic visions: beasts, horns, a figure like a son of man, and timelines that have been argued over for two thousand years. This half founded a genre and shaped the imagery of Revelation.",
      "The line worth carrying out of it is the three men's answer before the furnace — that God is able to deliver them, and that if he does not, they still will not bow. The conditional is the point.",
    ],
    startHere:
      "Chapter 3, the furnace, for the book's ethical centre. Chapter 7 if you want the visionary half and are ready for it.",
    themes: ["exile", "resistance", "empires", "vision"],
    keyChapters: [
      {
        chapter: 3,
        why: "The furnace, and the answer that holds whether or not they are rescued.",
      },
      {
        chapter: 6,
        why: "The lions' den, which is about a law designed to make faithfulness illegal.",
      },
      {
        chapter: 7,
        why: "The beasts and the one like a son of man, the passage Jesus applies to himself.",
      },
    ],
    known: {
      text: "If it happens, our God whom we serve is able to deliver us from the burning fiery furnace; and he will deliver us out of your hand, O king.",
      ref: "Daniel 3:17",
    },
    misread:
      "That the stories are for children and the visions are for prophecy charts. The stories are about political resistance under pressure, and the visions were written to encourage people being killed for their practice.",
    faq: [
      {
        q: "What is the book of Daniel about?",
        a: "Two halves: court narratives about keeping faith under foreign rule, and apocalyptic visions about empires rising and falling. Both were written for people under pressure to conform.",
      },
      {
        q: "When was Daniel written?",
        a: "The traditional view places it in the sixth century BC. Most modern scholars date the visions to the second century BC, during the persecution under Antiochus IV, on the basis of the historical detail they contain.",
      },
    ],
  },
  {
    slug: "hosea",
    name: "Hosea",
    testament: "old",
    genre: "prophecy",
    order: 28,
    chapters: 14,
    author: "Hosea",
    written: "Eighth century BC",
    audience: "The northern kingdom shortly before its destruction",
    argument:
      "A prophet told to marry an unfaithful woman and stay married to her, as a message that could not be delivered any other way.",
    summary: [
      "Hosea's ministry was to the northern kingdom in its last decades, and the book opens with an instruction that has troubled readers ever since: marry a promiscuous woman, and later, take her back.",
      "The marriage is the message. Israel's covenant unfaithfulness is described not as breaking a legal contract but as adultery, with everything that word carries — betrayal, humiliation, and a love that continues past the point where it makes sense.",
      "Its most-quoted line is picked up by Jesus twice: God desires mercy rather than sacrifice, which in context is an attack on a religion that was performing correctly and behaving appallingly.",
    ],
    startHere:
      "Chapter 11, where the metaphor shifts from marriage to parenthood and the book is at its most tender.",
    themes: ["unfaithfulness", "covenant love", "judgement", "return"],
    keyChapters: [
      {
        chapter: 1,
        why: "The instruction to marry, and the children given names that are the message.",
      },
      {
        chapter: 6,
        why: "I desire mercy and not sacrifice — a line Jesus quotes twice in Matthew, both times against people complaining about the company he was keeping.",
      },
      {
        chapter: 11,
        why: "The metaphor shifts to a parent teaching a child to walk, and the book becomes tender.",
      },
    ],
    known: {
      text: "For I desire mercy, and not sacrifice; and the knowledge of God more than burnt offerings.",
      ref: "Hosea 6:6",
    },
    misread:
      "That the marriage is an allegory the prophet only preached. The text presents it as something he was told to do and did, which is what makes the book so uncomfortable.",
    faq: [
      {
        q: "Why did Hosea marry a prostitute?",
        a: "As a sign-act. The book presents the marriage as instructed, and the relationship — betrayal followed by costly restoration — is the message about Israel's covenant unfaithfulness.",
      },
      {
        q: "What does I desire mercy and not sacrifice mean?",
        a: "It attacks religion performed correctly by people behaving badly. Jesus quotes it twice in Matthew, both times against critics complaining about who he was eating with.",
      },
    ],
  },
  {
    slug: "joel",
    name: "Joel",
    testament: "old",
    genre: "prophecy",
    order: 29,
    chapters: 3,
    author: "Joel",
    written:
      "Uncertain; proposals range from the ninth to the fourth century BC",
    audience: "A community that has just lost a harvest",
    argument:
      "A locust plague read as a warning, and the promise Peter quotes on the day the church begins.",
    summary: [
      "Joel opens with a description of a locust invasion so total that the priests have nothing left to offer. Whether the locusts are literal, an image for an invading army, or both, the book treats the catastrophe as a summons.",
      "Its call is to return with fasting and weeping, and specifically to rend your hearts and not your garments — a line aimed directly at the performance of repentance.",
      "The third chapter turns to promise, and one paragraph of it became the text Peter preached from at Pentecost: the Spirit poured out on all flesh, sons and daughters, old and young.",
    ],
    startHere:
      "Chapter 2, which contains both the call to return and the promise Peter later quotes.",
    themes: ["locusts", "the day of the Lord", "repentance", "the Spirit"],
    keyChapters: [
      {
        chapter: 1,
        why: "The locust devastation, described in terms that leave the priests with nothing to offer.",
      },
      {
        chapter: 2,
        why: "Rend your hearts and not your garments, and the promise Peter quotes at Pentecost.",
      },
      {
        chapter: 3,
        why: "The judgement of the nations, and the reversal of the swords-into-ploughshares image.",
      },
    ],
    known: {
      text: "It will happen afterward, that I will pour out my Spirit on all flesh; and your sons and your daughters will prophesy.",
      ref: "Joel 2:28",
    },
    misread:
      "That it is only about locusts. The insects occupy the first chapter; the book's subject is what a community does when something arrives that it cannot fix.",
    faq: [
      {
        q: "What is the book of Joel about?",
        a: "A devastating locust plague treated as a summons to return, followed by the promise of the Spirit poured out on all people — the passage Peter quotes at Pentecost.",
      },
      {
        q: "Why does Peter quote Joel in Acts 2?",
        a: "Because Joel 2:28 promises the Spirit given to everyone regardless of age, sex or status, which is precisely what Peter claims is happening in front of the crowd.",
      },
    ],
  },
  {
    slug: "amos",
    name: "Amos",
    testament: "old",
    genre: "prophecy",
    order: 30,
    chapters: 9,
    author: "Amos, a shepherd and fig farmer from Tekoa",
    written: "Eighth century BC",
    audience: "A prosperous kingdom at the height of its confidence",
    argument:
      "A farmer walks north into the wealthiest years the northern kingdom ever had and tells them their worship is offensive because of how they treat the poor.",
    summary: [
      "Amos was not a professional prophet — he says so explicitly — but a herdsman who arrived from the southern kingdom during a period of prosperity and told the north it was finished.",
      "His charge is economic and specific: selling the needy for a pair of sandals, trampling the poor, taking bribes, dishonest scales, and doing all of this while maintaining an impeccable religious calendar. Chapter 5 contains the most withering rejection of worship in the Bible — the festivals despised, the songs refused — followed by the demand that justice roll down like water.",
      "It is nine chapters and it is relentless, with only the last few verses turning toward restoration.",
    ],
    startHere:
      "Chapter 5, which contains the rejection of the festivals and the line about justice, and is the book in miniature.",
    themes: ["justice", "economics", "worship rejected", "the poor"],
    keyChapters: [
      {
        chapter: 1,
        why: "The oracles against the neighbours, which lull the audience before turning on them.",
      },
      {
        chapter: 5,
        why: "I hate your festivals — the most withering rejection of worship in the Bible.",
      },
      {
        chapter: 7,
        why: "The confrontation with the priest at Bethel, where Amos insists he is not a professional prophet.",
      },
    ],
    known: {
      text: "But let justice roll on like rivers, and righteousness like a mighty stream.",
      ref: "Amos 5:24",
    },
    misread:
      "That the justice line is a general aspiration. It is the alternative offered to religious festivals God has just said he hates, and the contrast is the argument.",
    faq: [
      {
        q: "What is the main message of Amos?",
        a: "That worship performed by a society exploiting its poor is offensive rather than neutral. The famous call for justice is stated as the alternative to festivals God says he despises.",
      },
      {
        q: "Who was Amos?",
        a: "A herdsman and tender of sycamore figs from Tekoa in the south, who insists he is not a prophet by profession, sent north during the region's most prosperous years.",
      },
    ],
  },
  {
    slug: "obadiah",
    name: "Obadiah",
    testament: "old",
    genre: "prophecy",
    order: 31,
    chapters: 1,
    author: "Obadiah",
    written: "Probably shortly after 586 BC",
    audience: "Judah, watching a neighbour profit from its destruction",
    argument:
      "The shortest book in the Old Testament, twenty-one verses long, and all of it aimed at a nation that stood by and watched.",
    summary: [
      "Obadiah is a single chapter addressed to Edom, Israel's neighbour and, in the tradition, its relative — Edom descends from Esau, Israel from Jacob.",
      "The charge is not that Edom attacked Jerusalem but that it stood at the crossroads while the city fell, gloated, and cut off those escaping. The specific sin named is standing aside on the day of disaster.",
      "Its central line states the principle the whole book turns on: as you have done, it will be done to you.",
    ],
    startHere:
      "The whole thing. It is twenty-one verses and takes three minutes.",
    themes: ["the bystander", "pride", "reversal", "Edom"],
    keyChapters: [
      {
        chapter: 1,
        why: "The whole book: twenty-one verses on standing aside while a neighbour is destroyed.",
      },
    ],
    known: {
      text: "As you have done, it will be done to you. Your deeds will return upon your own head.",
      ref: "Obadiah 1:15",
    },
    misread:
      "That it is a minor book about an ancient grudge. Its subject is the guilt of the bystander, which is one of the few places in Scripture where that is addressed directly.",
    faq: [
      {
        q: "How long is the book of Obadiah?",
        a: "Twenty-one verses in a single chapter — the shortest book in the Old Testament.",
      },
      {
        q: "Who is Obadiah written against?",
        a: "Edom, for standing by while Jerusalem was destroyed and profiting from it. The offence named is inaction and gloating rather than attack.",
      },
    ],
  },
  {
    slug: "jonah",
    name: "Jonah",
    testament: "old",
    genre: "prophecy",
    order: 32,
    chapters: 4,
    author: "Anonymous",
    written: "Likely post-exilic",
    audience: "People who would rather their enemies were not forgiven",
    argument:
      "A prophet who succeeds completely and is furious about it, in a book whose last word is a question nobody answers.",
    summary: [
      "Everybody knows the fish. Almost nobody knows chapter 4, which is what the book is actually about. Jonah is sent to Nineveh, the capital of an empire that had brutalised his people. He runs. He is brought back. He preaches a single sentence, the city repents, and God relents.",
      "Then Jonah sits outside the city and tells God that this is exactly why he ran — because he knew God was merciful, and he did not want these people forgiven. He asks to die.",
      "The book ends with God asking whether he should not have compassion on a city of a hundred and twenty thousand people who do not know their right hand from their left. Jonah does not reply. Neither does the narrator.",
    ],
    startHere:
      "Chapter 4. If you only ever read one chapter of Jonah, this is the one that changes what the other three are about.",
    themes: ["mercy", "running", "enemies", "anger"],
    keyChapters: [
      {
        chapter: 1,
        why: "The flight and the storm, and sailors who behave better than the prophet.",
      },
      {
        chapter: 3,
        why: "A five-word sermon and the largest recorded repentance in Scripture.",
      },
      {
        chapter: 4,
        why: "The chapter the book is actually about, and the question it ends on.",
      },
    ],
    known: {
      text: "Salvation belongs to Yahweh.",
      ref: "Jonah 2:9",
    },
    misread:
      "That it is a story about disobedience and a fish. The fish takes three verses; the argument is about a man who cannot bear his enemies being spared, and the book leaves his objection standing.",
    faq: [
      {
        q: "What is the point of the book of Jonah?",
        a: "Chapter 4. Jonah's complaint is that God is merciful to people he wanted destroyed, and the book ends on God's unanswered question about whether he should have compassion on them.",
      },
      {
        q: "How long is Jonah?",
        a: "Four short chapters — about ten minutes to read in full, and the last one is the one that matters most.",
      },
    ],
  },
  {
    slug: "micah",
    name: "Micah",
    testament: "old",
    genre: "prophecy",
    order: 33,
    chapters: 7,
    author: "Micah of Moresheth",
    written: "Eighth century BC",
    audience: "Both kingdoms, and their leaders in particular",
    argument:
      "A contemporary of Isaiah writing from a village rather than the capital, with the clearest one-line summary of the prophetic demand in the Bible.",
    summary: [
      "Micah prophesied to both kingdoms in the eighth century, and his complaint is against those with power — rulers who hate good and love evil, prophets who prophesy for money, judges who take bribes.",
      "He alternates between judgement and promise in a way that can be disorienting on a first read: a passage announcing Jerusalem's ruin sits next to one of the best-known promises of peace, swords into ploughshares, which he shares with Isaiah.",
      "Chapter 6 stages a courtroom scene where God brings a case against his own people, and it produces the summary that has served as a definition of religion for three thousand years: act justly, love mercy, walk humbly.",
    ],
    startHere:
      "Chapter 6, the courtroom, which ends in the verse everyone quotes and gives it the setting it needs.",
    themes: ["justice", "leadership", "Bethlehem", "humility"],
    keyChapters: [
      {
        chapter: 4,
        why: "Swords into ploughshares, shared almost word for word with Isaiah.",
      },
      {
        chapter: 5,
        why: "The ruler who comes from Bethlehem, a town small enough to be beneath notice, quoted by the scribes in Matthew's nativity.",
      },
      {
        chapter: 6,
        why: "The courtroom scene where God brings a case against his own people, ending in the verdict everybody quotes: act justly, love mercy, walk humbly.",
      },
    ],
    known: {
      text: "He has shown you, O man, what is good. What does Yahweh require of you, but to act justly, to love mercy, and to walk humbly with your God?",
      ref: "Micah 6:8",
    },
    misread:
      "That 6:8 is a gentle summary of the good life. It is the verdict in a legal case God is bringing against his people, answering a sarcastic question about whether thousands of rams would help.",
    faq: [
      {
        q: "What does Micah 6:8 mean?",
        a: "It answers a question raised in a courtroom scene: what does God actually want, given that sacrifices are not it. The answer is justice, mercy and humility, and it is a verdict rather than a proverb.",
      },
      {
        q: "Was Micah a contemporary of Isaiah?",
        a: "Yes, in the eighth century BC. They share the swords-into-ploughshares passage almost word for word, though Micah writes from a rural town rather than from Jerusalem.",
      },
    ],
  },
  {
    slug: "nahum",
    name: "Nahum",
    testament: "old",
    genre: "prophecy",
    order: 34,
    chapters: 3,
    author: "Nahum of Elkosh",
    written: "Seventh century BC, before Nineveh's fall in 612 BC",
    audience: "People living under Assyrian occupation",
    argument:
      "The sequel to Jonah, a century later: the same city, no repentance this time, and a book that is unapologetically glad it is going to fall.",
    summary: [
      "Nahum announces the destruction of Nineveh, and it is worth reading immediately after Jonah to feel the difference. Jonah's Nineveh repents and is spared; Nahum's has spent another century doing what Assyria did to conquered peoples, and the book celebrates its coming fall in vivid detail.",
      "Modern readers find the tone difficult. It is important to know who is speaking: a subject people under an empire notorious for mass deportation and public atrocity, being told the empire will end.",
      "Between the judgement oracles sits one of the gentlest verses in the prophets, and its placement is the book's argument in miniature.",
    ],
    startHere:
      "Chapter 1, which holds both halves — the fury and the refuge — in twelve verses.",
    themes: ["empire", "judgement", "refuge", "the fall of Nineveh"],
    keyChapters: [
      {
        chapter: 1,
        why: "The acrostic poem holding both the fury and the line about a stronghold in trouble.",
      },
      {
        chapter: 3,
        why: "The city's fall described in detail, from the point of view of people it had occupied.",
      },
    ],
    known: {
      text: "Yahweh is good, a stronghold in the day of trouble; and he knows those who take refuge in him.",
      ref: "Nahum 1:7",
    },
    misread:
      "That the book is simple vengeance. It is written from under an occupation, and read that way it is a claim that empires are accountable — which is a different thing from personal revenge.",
    faq: [
      {
        q: "How is Nahum related to Jonah?",
        a: "Both concern Nineveh. Jonah's Nineveh repents and is spared; Nahum, roughly a century later, announces its destruction after another hundred years of Assyrian brutality.",
      },
      {
        q: "Why is Nahum so violent?",
        a: "Because it is written by a subject people about the empire occupying them. The genre is oracle against the nations, and the claim it makes is that imperial power is accountable.",
      },
    ],
  },
  {
    slug: "habakkuk",
    name: "Habakkuk",
    testament: "old",
    genre: "prophecy",
    order: 35,
    chapters: 3,
    author: "Habakkuk",
    written: "Late seventh century BC",
    audience: "Anyone whose question is why God does nothing",
    argument:
      "The only prophetic book that is a conversation rather than a proclamation — the prophet complains, God answers, and the answer makes it worse before it makes it better.",
    summary: [
      "Habakkuk opens with a complaint: how long must I call for help and you not listen. The answer he receives is that God is raising up the Babylonians, which is not a comfort — it is worse than the problem.",
      "So he complains again, more sharply: how can a holy God use a nation more wicked than the one being punished. And then he does something unusual. He says he will stand at his watchpost and wait to see what will be said to him.",
      "The third chapter is a psalm, and its ending is one of the most remarkable statements in the Old Testament: though the fig tree does not blossom and there are no cattle in the stalls, yet I will rejoice. Nothing has been resolved. The posture has changed.",
    ],
    startHere:
      "Chapter 1 through to the end. It is three short chapters and the argument only works read as a whole.",
    themes: ["complaint", "waiting", "faith", "rejoicing anyway"],
    keyChapters: [
      {
        chapter: 1,
        why: "The complaint and the answer that makes it worse — God is raising up the Babylonians.",
      },
      {
        chapter: 2,
        why: "Standing at the watchpost, and the line about the righteous living by faith.",
      },
      {
        chapter: 3,
        why: "The closing psalm — though the fig tree does not blossom, yet I will rejoice.",
      },
    ],
    known: {
      text: "Yahweh, the Lord, is my strength. He makes my feet like deer's feet, and enables me to go in high places.",
      ref: "Habakkuk 3:19",
    },
    misread:
      "That it ends with the problem solved. The final chapter explicitly imagines total agricultural failure and commits to rejoicing anyway — the situation is not fixed, and the book says so.",
    faq: [
      {
        q: "What is the book of Habakkuk about?",
        a: "A prophet arguing with God about why injustice goes unanswered, and being given an answer he finds harder than the question. It ends in commitment rather than resolution.",
      },
      {
        q: "What does the just shall live by faith mean?",
        a: "Habakkuk 2:4, quoted three times in the New Testament. In its original setting it contrasts the proud, who are unsteady, with the righteous, who survive the coming upheaval by faithfulness.",
      },
    ],
  },
  {
    slug: "zephaniah",
    name: "Zephaniah",
    testament: "old",
    genre: "prophecy",
    order: 36,
    chapters: 3,
    author: "Zephaniah, given a royal genealogy in the opening verse",
    written: "Seventh century BC, during Josiah's reign",
    audience: "Jerusalem, shortly before its last reform",
    argument:
      "Three chapters that are almost entirely judgement and then end with God singing, which is an image that appears nowhere else in the Bible.",
    summary: [
      "Zephaniah announces the day of the Lord in sweeping terms — a sweeping away of everything from the face of the earth — and then narrows to Jerusalem's officials, judges, prophets and priests.",
      "The middle chapter turns outward to the surrounding nations, and the third returns to the city with a charge of accepting no correction.",
      "Then, in the last few verses, the book does something startling. God is described as rejoicing over his people with singing, and quieting them with his love. After two and a half chapters of judgement, the reversal is abrupt and deliberate.",
    ],
    startHere:
      "Chapter 3, which contains both the sharpest indictment and the closing lines that everybody who knows this book knows.",
    themes: ["the day of the Lord", "complacency", "the remnant", "singing"],
    keyChapters: [
      {
        chapter: 1,
        why: "The sweeping judgement, aimed particularly at those who say God will do nothing either way.",
      },
      {
        chapter: 3,
        why: "The turn at the end, where God rejoices over his people with singing.",
      },
    ],
    known: {
      text: "Yahweh, your God, is among you, a mighty one who will save. He will rejoice over you with joy. He will calm you in his love. He will rejoice over you with singing.",
      ref: "Zephaniah 3:17",
    },
    misread:
      "That the famous verse is the tone of the book. It is the last few lines of three chapters of judgement, and its force comes from what precedes it.",
    faq: [
      {
        q: "What is Zephaniah about?",
        a: "The day of the Lord, announced against Judah and the nations, ending with a short and startling passage about God rejoicing over his people with singing.",
      },
      {
        q: "When was Zephaniah written?",
        a: "During the reign of Josiah in the seventh century BC, most likely before his reforms — which the book's indictment of Jerusalem's leadership would suit.",
      },
    ],
  },
  {
    slug: "haggai",
    name: "Haggai",
    testament: "old",
    genre: "prophecy",
    order: 37,
    chapters: 2,
    author: "Haggai",
    written: "520 BC, dated precisely within the book",
    audience: "Returned exiles who had stopped building",
    argument:
      "Two chapters, four dated messages, one subject: you are living in panelled houses and the temple is still a ruin.",
    summary: [
      "Haggai is the most precisely dated book in the Old Testament — each message carries a day, month and regnal year, all within about four months of 520 BC.",
      "The situation is that the returned exiles began rebuilding the temple, met opposition, and stopped for roughly sixteen years while getting on with their own houses. Haggai's charge is exactly that, and the phrase he returns to is an instruction to consider your ways.",
      "It works. The building restarts within weeks, which makes Haggai one of the very few prophets in the Bible whose audience does what he says.",
    ],
    startHere: "Chapter 1, which is the whole argument in fifteen verses.",
    themes: ["priorities", "rebuilding", "dated messages", "glory"],
    keyChapters: [
      {
        chapter: 1,
        why: "Consider your ways — panelled houses while the temple lies in ruins.",
      },
      {
        chapter: 2,
        why: "The promise that the latter glory will exceed the former, said to people looking at a smaller building.",
      },
    ],
    known: {
      text: "Now therefore this is what Yahweh of Armies says: 'Consider your ways.",
      ref: "Haggai 1:5",
    },
    misread:
      "That it is about church buildings. The charge is about priorities revealed by where effort actually goes, which the panelled-house comparison makes concrete rather than abstract.",
    faq: [
      {
        q: "What is the book of Haggai about?",
        a: "Getting the temple rebuilt. The returned exiles had stopped for sixteen years and built their own houses; Haggai's four short messages restart the work within weeks.",
      },
      {
        q: "How long is Haggai?",
        a: "Two chapters and thirty-eight verses. It is one of the shortest books in the Old Testament and one of the few whose audience acted on it.",
      },
    ],
  },
  {
    slug: "zechariah",
    name: "Zechariah",
    testament: "old",
    genre: "prophecy",
    order: 38,
    chapters: 14,
    author: "Zechariah, with the second half widely attributed to another hand",
    written: "From 520 BC; chapters 9 to 14 possibly later",
    audience: "The same rebuilding community Haggai addressed",
    argument:
      "Eight night visions and then a stranger second half, containing more material the Gospels quote at the crucifixion than any other prophet except Isaiah.",
    summary: [
      "Zechariah begins alongside Haggai and with the same practical goal — get the temple finished — but goes about it through a sequence of eight night visions, each explained by an interpreting angel.",
      "The first half's argument is that the small, discouraging work in front of them matters, and its most quoted line makes the case: not by might, nor by power, but by my Spirit.",
      "The second half changes register entirely and supplies images the Gospel writers reach for repeatedly: a king entering on a donkey, thirty pieces of silver, a shepherd struck and the sheep scattered, one they have pierced.",
    ],
    startHere:
      "Chapter 4, for the vision of the lampstand and the line about might and power, which is the book's centre of gravity.",
    themes: ["night visions", "rebuilding", "the coming king", "cleansing"],
    keyChapters: [
      {
        chapter: 3,
        why: "Joshua the high priest in filthy clothes, re-dressed in front of the accuser.",
      },
      {
        chapter: 4,
        why: "Not by might nor by power, said to a governor with inadequate resources.",
      },
      {
        chapter: 9,
        why: "The king entering on a donkey, quoted in all four gospels.",
      },
    ],
    known: {
      text: "Not by might, nor by power, but by my Spirit,' says Yahweh of Armies.",
      ref: "Zechariah 4:6",
    },
    misread:
      "That the famous verse is about spiritual effort generally. It is spoken to a governor rebuilding a small temple with inadequate resources, about that specific job.",
    faq: [
      {
        q: "What are the visions in Zechariah?",
        a: "Eight night visions in the first six chapters — horsemen, a lampstand, a flying scroll, a woman in a basket and others — each interpreted by an angel and all aimed at encouraging the rebuilding.",
      },
      {
        q: "Why do the Gospels quote Zechariah so much?",
        a: "Its second half supplies several images the passion narratives use: the king on a donkey, the thirty pieces of silver, the struck shepherd, and the one who is pierced.",
      },
    ],
  },
  {
    slug: "malachi",
    name: "Malachi",
    testament: "old",
    genre: "prophecy",
    order: 39,
    chapters: 4,
    author: 'Malachi, or possibly a title meaning "my messenger"',
    written: "Fifth century BC",
    audience: "A community that had rebuilt the temple and gone flat",
    argument:
      "The last book of the Old Testament, structured as a series of arguments in which the people keep answering back.",
    summary: [
      "Malachi is built on a distinctive rhetorical pattern: God makes a charge, the people respond with a question — how have we done that? — and the charge is then spelled out. It happens six times, and the effect is of a dispute rather than a sermon.",
      "The complaints concern blemished sacrifices, faithless divorce, wearying God with words, and withholding what belongs to the temple. The community is not in crisis; it has simply gone through the motions for long enough that the motions are all that is left.",
      "It ends the Old Testament with a promise of a messenger to come, and four hundred years of silence follow it in the Christian ordering.",
    ],
    startHere:
      "Chapter 3, which contains the tithe passage and the promise of the messenger.",
    themes: [
      "disputation",
      "half-hearted worship",
      "covenant faithfulness",
      "the messenger",
    ],
    keyChapters: [
      {
        chapter: 1,
        why: "Blemished animals offered to God and the challenge to try them on the governor.",
      },
      {
        chapter: 2,
        why: "The passage on faithless divorce, and the line about God hating it.",
      },
      {
        chapter: 3,
        why: "The tithe passage, and the messenger who will come suddenly to his temple.",
      },
    ],
    known: {
      text: 'Bring the whole tithe into the storehouse, that there may be food in my house, and test me now in this," says Yahweh of Armies',
      ref: "Malachi 3:10",
    },
    misread:
      "That the tithe passage is a general promise of financial return. It is addressed to a specific community withholding food from the temple stores, in a covenant with specified terms, and the storehouse in question held provisions for priests.",
    faq: [
      {
        q: "What is the book of Malachi about?",
        a: "A dispute in six rounds between God and a community going through the motions — blemished offerings, faithless divorce, withheld tithes — ending with the promise of a messenger to come.",
      },
      {
        q: "Is Malachi the last book of the Old Testament?",
        a: "In the Christian ordering, yes. The Hebrew Bible arranges the books differently and ends with Chronicles.",
      },
    ],
  },
];
